import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import Info from '../../../Info/Info';
import Pills from '../../../Pills/Pill';
import SearchBar from '../../../SearchBar/SearchBar';
import { PillStatus, PillVariant } from '../../../Pills/type';

import { SpaceSearchOptions, SpaceAssetsModalSearchProps } from '../../utils/types';
import { extractTextFromJSX } from './helper';

const PillArgs = {
  id: "new-pill",
  status: "default" as PillStatus,
  variant: "label" as PillVariant,
  background: "#6C5CE7",
  isSingleton: true,
  shouldHaveBorder: false,
  items: [{ id: "new", text: "NEW" }]
}
const NewPill = <Pills {...PillArgs} />

const HistoryInfoComponent = (props) => {
  return (
    <Info
      testId="cs-entries-recent-searches-info"
      content={
        <ul>
          <li>Use '+' or '-' to include or exclude a word (e.g., brown +fox, brown -fox) {NewPill}</li>
          <li>Use double quotes for exact match (e.g., “Brown fox”)</li>
          <li>Use first few letters to search by start of the word (e.g., Brow)</li>
          <li>Prefix * to search by middle or end of the word (e.g., *own)</li>
        </ul>
      }
      type="light"
      dismissable={false}
      version="v2"
      onCancelClick={props.onInfoCancelClick}
      collapsedText={props.showInfo ? "" : "Fine tune your search! Expand to learn more."}
      collapsedClick={(val) => {
        props.onInfoCancelClick(val)
      }}
    />
  );
};

const SpaceAssetsModalSearch: React.FC<SpaceAssetsModalSearchProps> = (props) => {
  const {
    searchFor = '',
    searchDropDownList,
    searchTypes,
    spaceAssetsSearchOptions,
    setSpaceAssetsSearchOptions,
    shouldDisabled,
    setIsSuggestions,
    shouldClearSearchFilters,
    setShouldClearSearchFilters,
    setShowSelectedAssets,
    isResponsive = false,
    withDropdown: withDropdownProp,
    placeholder,
  } = props;
  const showDropdown = withDropdownProp !== undefined ? withDropdownProp : true;
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useMemo(
    () => debounce((config: SpaceSearchOptions) => setSpaceAssetsSearchOptions(config), 300),
    []
  );
  const [showInfo, setShowInfo] = useState(true);

  // Applies styles directly to the DOM elements to disable & enable the search bar and button
  // when the search type is set to suggestions
  useEffect(() => {
    if (!searchContainerRef.current) return;

    const addRemoveDisableClass = (selector: string, isInput: boolean = false) => {
      const ele = searchContainerRef.current!.querySelector(selector);
      if (ele && !isInput) {
        ele.classList.toggle('SpaceAssetsModalSearch--disabled', shouldDisabled);
      } else if (ele && isInput) {
        ele.classList.toggle('SpaceAssetsModalSearch__input--disabled', shouldDisabled);
      }
    };

    addRemoveDisableClass('.SearchBar__input');
    addRemoveDisableClass('.SearchBar__button');
    addRemoveDisableClass('.TextInput__input', true);
  }, [shouldDisabled]);

  useEffect(() => {
    if (shouldClearSearchFilters) {
      setSpaceAssetsSearchOptions({
        searchType: searchTypes.ALL,
        searchText: ''
      });
      setShouldClearSearchFilters(false);
    }
  }, [shouldClearSearchFilters]);

  const handleSearchSubmit = useCallback(
    ({ searchValue }: { searchValue: string }) => {
      const newOptions = {
        ...spaceAssetsSearchOptions,
        searchText: searchValue
      };
      debouncedSearch(newOptions);
    },
    [spaceAssetsSearchOptions, debouncedSearch]
  );

  const handleSearchClear = useCallback(() => {
    setSpaceAssetsSearchOptions({
      searchType: searchTypes.ALL,
      searchText: ''
    });
  }, [searchTypes]);

  const handleSearchTypeChange = useCallback(
    (type: string) => {
      setShowSelectedAssets(false);
      const newOptions = {
        ...spaceAssetsSearchOptions,
        searchType: type
      };
      debouncedSearch(newOptions);
      if (extractTextFromJSX(type, 1) === searchTypes.SUGGESTIONS) {
        setIsSuggestions(true);
      } else {
        setIsSuggestions(false);
      }
    },
    [spaceAssetsSearchOptions, debouncedSearch]
  );

  const dropdownList = useMemo(
    () =>
      searchDropDownList.map((item) => ({
        ...item,
        action: () => handleSearchTypeChange(item.value as string),
        default: item.value === spaceAssetsSearchOptions.searchType
      })),
    [handleSearchTypeChange, spaceAssetsSearchOptions.searchType, searchDropDownList]
  );

  const onInfoCancelClick = (toggle) => {
    setShowInfo(toggle);
  };

  return (
    <div className={`SpaceAssetsModalSearch ${isResponsive ? `SpaceAssetsModalSearch--resp_screen` : ``}`} ref={searchContainerRef}>
      <SearchBar
        testId={`${searchFor}-search-bar`}
        searchConfig={{
          searchPlaceholder: placeholder || 'Search assets by name, type, tag or folder',
          historyInfo: <HistoryInfoComponent onInfoCancelClick={onInfoCancelClick} showInfo={showInfo} />
        }}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
        withDropdown={showDropdown}
        dropdownList={dropdownList}
        handleRecentSearchClear={handleSearchClear}
        inputText={spaceAssetsSearchOptions.searchText}
        componentResponsiveness={{
          isResponsive: isResponsive,
          showOnlySearchIcon: isResponsive,
          searchIconSize: 'small'
        }}
        recentSearches={[]}
      />
    </div>
  );
};

export default React.memo(SpaceAssetsModalSearch);
